import React from 'react';
import { Alert } from '../../../shared/components/Alert';
import { PageContainer } from '../../../shared/components/PageContainer';
import { Skeleton } from '../../../shared/components/Skeleton';
import { formatMoney } from '../components/format';
import { PURCHASE_STATUS_LABELS } from '../components/format';
import type { PurchaseRequestStatus } from '../api';
import { usePurchaseDashboard } from '../hooks';

export default function RelatoriosComprasPage() {
  const dashboard = usePurchaseDashboard();
  const data = dashboard.data;
  const byStatus = Array.isArray(data?.byStatus)
    ? data.byStatus
    : Object.entries(data?.byStatus ?? {}).map(([status, count]) => ({ status, count }));

  return (
    <PageContainer title="Relatórios de compras" subtitle="Indicadores operacionais obtidos do ciclo real" breadcrumbs={[{ label: 'Compras', to: '/compras' }, { label: 'Relatórios' }]}>
      {dashboard.isError && <Alert variant="error">Não foi possível carregar os indicadores de compras.</Alert>}
      {dashboard.isLoading ? <Skeleton className="h-48 w-full" /> : (
        <>
          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label="Indicadores de compras">
            <Metric label="Solicitações" value={data?.totalRequests} />
            <Metric label="Aprovações pendentes" value={data?.pendingApprovals} />
            <Metric label="Exceções de cotação" value={data?.quotationExceptions} />
            <Metric label="Tempo médio de ciclo" value={data?.averageCycleHours != null ? `${Number(data.averageCycleHours).toLocaleString('pt-BR')} h` : undefined} />
            <Metric label="Economia estimada" value={formatMoney(data?.estimatedSavings, data?.estimatedSavingsCurrency ?? 'BRL')} />
            <Metric label="Fornecedores contratados" value={data?.contractedSuppliers} />
            <Metric label="Pagamentos pendentes" value={data?.pendingPayments} />
            <Metric label="Pagamentos vencidos" value={data?.overduePayments} />
          </section>
          <section className="rounded-lg border border-gray-200 bg-white p-5" aria-labelledby="requests-status-title">
            <h2 id="requests-status-title" className="font-semibold text-gray-900">Solicitações por status</h2>
            {byStatus.length === 0 ? <p className="mt-3 text-sm text-gray-500">Ainda não há dados para o período.</p> : (
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {byStatus.map((item) => <div key={item.status} className="flex items-center justify-between rounded-md bg-gray-50 px-4 py-3"><span className="text-sm text-gray-700">{PURCHASE_STATUS_LABELS[item.status as PurchaseRequestStatus] ?? 'Estado operacional'}</span><strong>{item.count}</strong></div>)}
              </div>
            )}
          </section>
        </>
      )}
    </PageContainer>
  );
}

function Metric({ label, value }: { label: string; value: React.ReactNode }) {
  return <div className="rounded-lg border border-gray-200 bg-white p-4"><p className="text-sm text-gray-500">{label}</p><p className="mt-2 text-2xl font-semibold text-gray-900">{value ?? '—'}</p></div>;
}
